import React, { useState, useCallback, useEffect } from "react";
import {
  Modal,
  Form,
  InputNumber,
  Checkbox,
  Button,
  Spin,
  message,
  Divider,
  Popconfirm,
  Space,
  Tooltip,
  Select,
  Radio,
  Input,
} from "antd";
import { FaFileInvoiceDollar } from "react-icons/fa";
import {
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  PlusOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { getImg } from "../../../Api/getImg";
import drEasyApi from "../../../Api/Customapi";
import { useInvoiceMutations } from "../../../hooks/useInvoice";
import { useClinics } from "../../../hooks/useClinics";
import { useQueryClient } from "@tanstack/react-query";
import { AddServicePanel } from "./Pannel";
import { MdOutlineMedicalServices } from "react-icons/md";
import {
  PrintConfirmModal,
  printInvoicePDF,
} from "../../PrintInvoice/PrintInvoice";
import CouponApplyModal from "./CouponApplyModal";

// ─── Price Helper ─────────────────────────────────────────────────────────────
/**
 * Returns the effective unit price for a service after applying its discount.
 * Only applies when showDiscount is true and offerPercentage is a valid number.
 *
 * @param {Object} service - Service object from GetMyServices
 * @returns {number} Effective price (original price if no valid discount)
 */
export const getEffectivePrice = (service) => {
  const basePrice = service?.price ?? 0;
  if (
    service?.offerPercentage != null &&
    service.offerPercentage > 0 &&
    service.offerPercentage <= 100
  ) {
    return parseFloat(
      (basePrice * (1 - service.offerPercentage / 100)).toFixed(2),
    );
  }
  return basePrice;
};

// ─── Service Price Tag ────────────────────────────────────────────────────────
/**
 * Small helper component that renders the price with discount badge
 * when the service has an active discount.
 */
const ServicePriceDisplay = ({ service, isMobile }) => {
  const effective = getEffectivePrice(service);
  const hasDiscount = service.offerPercentage > 0;

  return (
    <div
      className={`flex items-center gap-2 flex-wrap ${isMobile ? "text-xs" : "text-sm"}`}
    >
      {hasDiscount ? (
        <>
          {/* Crossed-out original */}
          <span className="line-through text-gray-400">
            EGP {Number(service.price).toFixed(2)}
          </span>
          {/* Effective price */}
          <span className="text-green-600 font-semibold">
            EGP {Number(service.priceAfterDiscount).toFixed(2)}
          </span>
          {/* Discount badge */}
          {/* <span className="bg-red-100 text-red-600 text-xs font-bold px-1.5 py-0.5 rounded-full">
            -{service.offerPercentage}%
          </span> */}
        </>
      ) : (
        <>
          <span className="text-gray-500">
            {service.price != null
              ? `EGP ${Number(service.price).toFixed(2)}`
              : "No price set"}
          </span>
          {service.showDuration && service.durationInMin != null && (
            <span className="text-gray-400">⏱ {service.durationInMin} min</span>
          )}
        </>
      )}
    </div>
  );
};

// ─── Main InvoiceModal ────────────────────────────────────────────────────────
const InvoiceModal = ({
  visible,
  onCancel,
  booking,
  isMobile,
  onSuccess,
  patient,
  editing,
  patientSessionId
}) => {
  const [form] = Form.useForm();
  const [selectedServices, setSelectedServices] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editServiceForm, setEditServiceForm] = useState({});
  const [previouslyPaidAmount, setPreviouslyPaidAmount] = useState(0);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [externalServices, setExternalServices] = useState([]);
  const queryClient = useQueryClient();
  const [couponModalVisible, setCouponModalVisible] = useState(false);
  const [pendingInvoiceId, setPendingInvoiceId] = useState(null);
  const [pendingInvoiceTotal, setPendingInvoiceTotal] = useState(0);

  const [isAddingService, setIsAddingService] = useState(false);
  const [newServiceData, setNewServiceData] = useState({
    serviceType: "existing",
    serviceId: null,
    externalServiceTitle: "",
    unitPrice: 0,
    quantity: 1,
  });
  const [isAddServiceSubmitting, setIsAddServiceSubmitting] = useState(false);

  const [printConfirmVisible, setPrintConfirmVisible] = useState(false);
  const [lastCreatedInvoiceData, setLastCreatedInvoiceData] = useState(null);

  const { editInvoiceService, removeInvoiceService } = useInvoiceMutations();
  const { clinicsQuery } = useClinics();

  const resetState = () => {
    form.resetFields();
    form.setFieldsValue({                          // ← ADD THIS BLOCK
      clinicId: booking?.clinicId || null,
    });
    setSelectedServices({});
    setExternalServices([]);
    setIsEditMode(false);
    // setInvoiceData(null);
    setPreviouslyPaidAmount(0);
    setEditingServiceId(null);
    setEditServiceForm({});
    setIsAddingService(false);
    setNewServiceData({
      serviceType: "existing",
      serviceId: null,
      externalServiceTitle: "",
      unitPrice: 0,
      quantity: 1,
    });
  };

  useEffect(() => {
    if (visible && booking?.invoiceId) {
      fetchInvoiceData(booking.invoiceId);
    } else if (visible) {
      resetState();
    }
  }, [visible, booking?.invoiceId]);

  const fetchInvoiceData = async (invoiceId) => {
    setIsLoadingInvoice(true);
    try {
      const response = await drEasyApi.get("/api/Invoice/Get", {
        params: { InvoiceId: invoiceId },
      });
      const invoice = response.data;
      setInvoiceData(invoice);
      setIsEditMode(true);
      setPreviouslyPaidAmount(invoice.paidAmount || 0);
      form.setFieldsValue({
        clinicId: invoice.clinicId || booking?.clinicId || null,
        visitPrice: invoice.visitPrice || 0,
        paidAmount: 0,
        discountAmount: invoice.discountAmount || 0,
        discountPer: invoice.discountPer || 0,
      });
      if (invoice.services?.length > 0) {
        const servicesMap = {};
        invoice.services.forEach((s) => {
          servicesMap[s.serviceId] = {
            quantity: s.quantity || 1,
            unitPrice: s.unitPrice || 0,
          };
        });
        setSelectedServices(servicesMap);
      }
    } catch (error) {
      message.error("Failed to load invoice data");
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  // ── When checking a service, default unitPrice = effective price (after discount) ──
  const handleServiceChange = useCallback(
    (serviceId, checked) => {
      if (checked) {
        const service = services.find((s) => s.id === serviceId);
        setSelectedServices((prev) => ({
          ...prev,
          [serviceId]: {
            quantity: 1,
            unitPrice: getEffectivePrice(service), // ✅ discounted price as default
          },
        }));
      } else {
        setSelectedServices((prev) => {
          const next = { ...prev };
          delete next[serviceId];
          return next;
        });
      }
    },
    [services],
  );

  const fetchDoctorServices = useCallback(async () => {
    setServicesLoading(true);
    try {
      const response = await drEasyApi.get("/api/Services/GetMyServices");
      setServices(response.data);
    } catch {
      message.error("Error fetching services");
    } finally {
      setServicesLoading(false);
    }
  }, [newServiceData]);

  const handleQuantityChange = useCallback((serviceId, quantity) => {
    setSelectedServices((prev) => ({
      ...prev,
      [serviceId]: { ...prev[serviceId], quantity: quantity || 1 },
    }));
  }, []);

  const handleUnitPriceChange = useCallback((serviceId, unitPrice) => {
    setSelectedServices((prev) => ({
      ...prev,
      [serviceId]: { ...prev[serviceId], unitPrice: unitPrice || 0 },
    }));
  }, []);

  const handleStartEditService = (service) => {
    setEditingServiceId(service.invoiceServiceId);
    setEditServiceForm({
      [service.invoiceServiceId]: {
        quantity: service.quantity,
        unitPrice: service.unitPrice,
      },
    });
  };

  const handleCancelEditService = () => {
    setEditingServiceId(null);
    setEditServiceForm({});
  };

  const handleSaveEditService = async (service) => {
    try {
      const editedData = editServiceForm[service.invoiceServiceId];
      if (!editedData || editedData.quantity < 1 || editedData.unitPrice < 0) {
        message.error("Please enter valid values");
        return;
      }
      await editInvoiceService.mutateAsync({
        invoiceId: booking.invoiceId,
        invoiceServiceId: service.invoiceServiceId,
        quantity: editedData.quantity,
        unitPrice: editedData.unitPrice,
      });
      await fetchInvoiceData(booking.invoiceId);
      setEditingServiceId(null);
      setEditServiceForm({});
    } catch (error) {
      console.error("Error editing service:", error);
    }
  };

  const handleDeleteService = async (invoiceServiceId) => {
    try {
      await removeInvoiceService.mutateAsync(invoiceServiceId);
      message.success("Service removed successfully");
      await fetchInvoiceData(booking.invoiceId);
    } catch (error) {
      message.error(error.message || "Failed to remove service");
    }
  };

  const updateEditServiceForm = (invoiceServiceId, field, value) => {
    setEditServiceForm((prev) => ({
      ...prev,
      [invoiceServiceId]: { ...prev[invoiceServiceId], [field]: value },
    }));
  };

  // ── When selecting a service in the "Add Service" panel, default to effective price ──
  const handleServiceSelect = (serviceId) => {
    const service = services.find((s) => s.id === serviceId);
    setNewServiceData({
      ...newServiceData,
      serviceId,
      unitPrice: getEffectivePrice(service), // ✅ discounted price as default
    });
  };

  const handleServiceTypeChange = (type) => {
    setNewServiceData({
      serviceType: type,
      serviceId: null,
      externalServiceTitle: "",
      unitPrice: 0,
      quantity: 1,
    });
  };

  const handleAddServiceToInvoice = async () => {
    if (
      newServiceData.serviceType === "existing" &&
      !newServiceData.serviceId
    ) {
      message.error("Please select a service");
      return;
    }
    if (
      newServiceData.serviceType === "external" &&
      !newServiceData.externalServiceTitle.trim()
    ) {
      message.error("Please enter service title");
      return;
    }
    if (newServiceData.quantity < 1) {
      message.error("Quantity must be at least 1");
      return;
    }
    if (newServiceData.unitPrice < 0) {
      message.error("Unit price must be positive");
      return;
    }

    if (isEditMode) {
      setIsAddServiceSubmitting(true);
      try {
        const payload =
          newServiceData.serviceType === "existing"
            ? {
              invoiceId: booking.invoiceId,
              serviceId: newServiceData.serviceId,
              externalServiceTitle:
                services.find((s) => s.id === newServiceData.serviceId)
                  ?.title || "",
              unitPrice: newServiceData.unitPrice,
              quantity: newServiceData.quantity,
            }
            : {
              invoiceId: booking.invoiceId,
              serviceId: null,
              externalServiceTitle: newServiceData.externalServiceTitle,
              unitPrice: newServiceData.unitPrice,
              quantity: newServiceData.quantity,
            };
        await drEasyApi.post("/api/Invoice/AddService", payload);
        message.success("Service added successfully");
        setIsAddingService(false);
        setNewServiceData({
          serviceType: "existing",
          serviceId: null,
          externalServiceTitle: "",
          unitPrice: 0,
          quantity: 1,
        });
        await fetchInvoiceData(booking.invoiceId);
      } catch (error) {
        message.error(error.response?.data?.message || "Failed to add service");
      } finally {
        setIsAddServiceSubmitting(false);
      }
      return;
    }

    if (newServiceData.serviceType === "existing") {
      const service = services.find((s) => s.id === newServiceData.serviceId);
      setSelectedServices((prev) => ({
        ...prev,
        [newServiceData.serviceId]: {
          quantity: newServiceData.quantity,
          unitPrice: newServiceData.unitPrice,
          title: service?.title || "",
        },
      }));
    } else {
      const tempId = `ext_${Date.now()}`;
      setExternalServices((prev) => [
        ...prev,
        {
          tempId,
          externalServiceTitle: newServiceData.externalServiceTitle,
          unitPrice: newServiceData.unitPrice,
          quantity: newServiceData.quantity,
        },
      ]);
    }
    setIsAddingService(false);
    setNewServiceData({
      serviceType: "existing",
      serviceId: null,
      externalServiceTitle: "",
      unitPrice: 0,
      quantity: 1,
    });
  };

  const handleCancelAddService = () => {
    setIsAddingService(false);
    setNewServiceData({
      serviceType: "existing",
      serviceId: null,
      externalServiceTitle: "",
      unitPrice: 0,
      quantity: 1,
    });
  };

  const removeExternalService = (tempId) => {
    setExternalServices((prev) => prev.filter((s) => s.tempId !== tempId));
  };

  // ─── Totals ───────────────────────────────────────────────────────────────
  const calculateTotals = useCallback(() => {
    const visitPrice = form.getFieldValue("visitPrice") || 0;
    const discountAmount = form.getFieldValue("discountAmount") || 0;
    const discountPer = form.getFieldValue("discountPer") || 0;
    const newPaidAmount = form.getFieldValue("paidAmount") || 0;

    let servicesTotal = 0;
    if (isEditMode && invoiceData?.services) {
      servicesTotal = invoiceData.services.reduce(
        (sum, s) => sum + s.unitPrice * s.quantity,
        0,
      );
    } else {
      servicesTotal =
        Object.values(selectedServices).reduce(
          (sum, d) => sum + d.unitPrice * d.quantity,
          0,
        ) +
        externalServices.reduce((sum, s) => sum + s.unitPrice * s.quantity, 0);
    }

    const subtotal = visitPrice + servicesTotal;
    const percentDiscount = (subtotal * discountPer) / 100;
    const totalDiscount = discountAmount + percentDiscount;
    const total = isEditMode ? invoiceData?.totals?.totalAmount : subtotal - totalDiscount;
    const totalPaidAmount = isEditMode
      ? previouslyPaidAmount + newPaidAmount
      : newPaidAmount;
    const remaining = isEditMode ? invoiceData?.remainingAmount : total - totalPaidAmount;

    return {
      visitPrice,
      servicesTotal,
      subtotal,
      percentDiscount,
      totalDiscount,
      total: Math.max(0, total),
      newPaidAmount,
      totalPaidAmount,
      previouslyPaidAmount: isEditMode ? previouslyPaidAmount : 0,
      remaining: Math.max(0, remaining),
    };
  }, [
    selectedServices,
    externalServices,
    form,
    isEditMode,
    invoiceData?.services,
    previouslyPaidAmount,
  ]);

  const [totals, setTotals] = useState(calculateTotals());
  useEffect(() => {
    setTotals(calculateTotals());
  }, [calculateTotals, selectedServices, externalServices, invoiceData]);
  useEffect(() => {
    fetchDoctorServices();
  }, []);

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      if (isEditMode) {
        await drEasyApi.put("/api/Invoice/Edit", {
          invoiceId: booking.invoiceId,
          visitPrice: values.visitPrice || 0,
          discountAmount: values.discountAmount || 0,
          discountPer: values.discountPer || 0,
          paidAmount: previouslyPaidAmount + (values.paidAmount || 0),
          clinicId: values.clinicId

        });

        // ✅ Build print data BEFORE resetting state
        const printServices = invoiceData.services.map((s) => ({
          title: s.title || s.externalServiceTitle || "Service",
          unitPrice: s.unitPrice,
          quantity: s.quantity,
        }));
        const invoiceSnapshot = {
          visitPrice: values.visitPrice || 0,
          services: printServices,
          discountAmount: values.discountAmount || 0,
          discountPer: values.discountPer || 0,
          paidAmount: previouslyPaidAmount,   // ← no new paidAmount here anymore
        };
        setLastCreatedInvoiceData({ invoice: invoiceSnapshot, booking });
        setPendingInvoiceTotal(totals.total);  // ← pass current total
        resetState();
        onSuccess?.(true);
        onCancel();
        setPendingInvoiceId(booking.invoiceId);
        setCouponModalVisible(true);
      } else {
        const existingServicesList = Object.entries(selectedServices).map(
          ([serviceId, data]) => {
            const item = {
              externalServiceTitle: data.title || "",
              unitPrice: data.unitPrice,
              quantity: data.quantity,
            };
            if (serviceId && serviceId !== "null" && serviceId !== "undefined") {
              item.serviceId = serviceId;
            }
            return item;
          },
        );

        const externalServicesList = externalServices.map((s) => ({
          externalServiceTitle: s.externalServiceTitle,
          unitPrice: s.unitPrice,
          quantity: s.quantity,
        }));

        const allServices = [...existingServicesList, ...externalServicesList];

        const res = await drEasyApi.post("/api/Invoice/Create", {
          visitPrice: values.visitPrice || 0,
          bookingId: booking?.id || null,
          patientSessionId: patientSessionId || null,
          userID: patient?.accountId || null,
          clinicId: values.clinicId || null,
          paidAmount: values.paidAmount || 0,
          discountAmount: values.discountAmount || 0,
          discountPer: values.discountPer || 0,
          services: allServices,
        });
        const response = await drEasyApi.get("/api/Invoice/Get", {
          params: { InvoiceId: res.data },
        });
        const invoice = response.data;
        setInvoiceData(invoice);
        queryClient.invalidateQueries({ queryKey: ["userAccount"] });
        queryClient.invalidateQueries({ queryKey: ["bookings"] });

        const builtServices = allServices.map((s, i) => {
          const matched = services?.find((ds) => ds.id === s.serviceId);
          return {
            title: matched?.title || s.externalServiceTitle || `Service ${i + 1}`,
            unitPrice: s.unitPrice,
            quantity: s.quantity,
          };
        });

        const invoiceSnapshot = {
          visitPrice: values.visitPrice || 0,
          services: builtServices,
          discountAmount: values.discountAmount || 0,
          discountPer: values.discountPer || 0,
          paidAmount: values.paidAmount || 0,
        };

        setLastCreatedInvoiceData({ invoice: invoiceSnapshot, booking });
        resetState();
        onSuccess?.(false);
        onCancel();
        setPendingInvoiceId(res.data);
        console.log(pendingInvoiceId, "pending");
        // ← from create response
        setCouponModalVisible(true);

      }
    } catch (error) {
      message.error(
        error.response?.data?.[0] ||
        `Failed to ${isEditMode ? "Continue" : "Continue"} invoice`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  const handlePrintConfirm = () => {
    if (lastCreatedInvoiceData)
      printInvoicePDF(
        lastCreatedInvoiceData.invoice,
        lastCreatedInvoiceData.booking,
      );
    setPrintConfirmVisible(false);
    setLastCreatedInvoiceData(null);
    message.success("Invoice created!");
  };

  const handlePrintSkip = () => {
    setPrintConfirmVisible(false);
    setLastCreatedInvoiceData(null);
    message.success("Invoice created!");
  };

  const handlePrint = () => {
    const values = form.getFieldsValue();
    let printServices = [];
    if (isEditMode && invoiceData?.services) {
      printServices = invoiceData.services.map((s) => ({
        title: s.title || s.externalServiceTitle || "Service",
        unitPrice: s.unitPrice,
        quantity: s.quantity,
      }));
    } else {
      printServices = [
        ...Object.entries(selectedServices).map(([serviceId, data]) => {
          const matched = services?.find((ds) => ds.id === serviceId);
          return {
            title: matched?.title || data.title || "Service",
            unitPrice: data.unitPrice,
            quantity: data.quantity,
          };
        }),
        ...externalServices.map((s) => ({
          title: s.externalServiceTitle || "External Service",
          unitPrice: s.unitPrice,
          quantity: s.quantity,
        })),
      ];
    }
    printInvoicePDF(
      {
        visitPrice: values.visitPrice || 0,
        services: printServices,
        discountAmount: values.discountAmount || 0,
        discountPer: values.discountPer || 0,
        paidAmount: isEditMode
          ? previouslyPaidAmount + (values.paidAmount || 0)
          : values.paidAmount || 0,
      },
      booking,
    );
  };

  const handleCancel = () => {
    resetState();
    onCancel();
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <Modal
        title={
          <div className="flex items-center">
            <div
              className={`${isMobile ? "w-6 h-6" : "w-8 h-8"} bg-gradient-to-r from-primary to-primary_hover rounded-full flex items-center justify-center ${isMobile ? "mr-2" : "mr-3"}`}
            >
              <FaFileInvoiceDollar
                className={`text-white ${isMobile ? "text-xs" : "text-sm"}`}
              />
            </div>
            <span className={isMobile ? "text-sm" : ""}>
              {isEditMode ? "Edit Invoice" : "Create Invoice"}
            </span>
          </div>
        }
        open={visible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          // <Button
          //   key="print"
          //   icon={<PrinterOutlined />}
          //   onClick={handlePrint}
          //   style={{
          //     background: "linear-gradient(135deg, #c9a84c, #e8c96a)",
          //     border: "none",
          //     color: "#fff",
          //   }}
          // >
          //   {isMobile ? "" : "Print"}
          // </Button>,
          <Button
            key="submit"
            type=""
            onClick={handleSubmit}
            loading={isSubmitting}
            className="bg-primary text-white"
          >
            {isEditMode ? "Update" : "Create"}
          </Button>,
        ]}
        width={isMobile ? "95vw" : 700}
        centered={isMobile}
      >
        <Spin spinning={isLoadingInvoice}>
          {(booking || patientSessionId) && (
            <div className="mb-4">
              <div
                className={`p-3 bg-primary/10 rounded-lg border border-primary ${isMobile ? "text-sm" : ""}`}
              >
                <div className="font-medium text-primary">
                  Patient: {booking?.patientName}
                </div>
                <div className="text-gray-600 text-sm">
                  Booking ID: #{booking?.bookingCode}
                </div>
                {isEditMode && (
                  <div className="text-blue-600 text-sm mt-1">
                    📝 Editing existing invoice
                  </div>
                )}
              </div>
            </div>
          )}

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              clinicId: booking?.clinicId || null,
              visitPrice: 0,
              paidAmount: 0,
              discountAmount: 0,
              discountPer: 0,
            }}
            onValuesChange={() => setTotals(calculateTotals())}
          >
            {/* {patient && !editing && ( */}
            <Form.Item
              name="clinicId"
              label={
                <span className={isMobile ? "text-sm" : ""}>Clinic</span>
              }
              rules={[{ required: true, message: "Please select a clinic" }]}
            >
              <Select
                placeholder="Select a clinic"
                size={isMobile ? "middle" : "large"}
                loading={clinicsQuery.isLoading}
                showSearch
                optionFilterProp="children"
              >
                {clinicsQuery.data?.clinics?.map((clinic) => (
                  <Select.Option
                    key={clinic.clinicId}
                    value={clinic.clinicId}
                  >
                    {clinic.clinicName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            {/* )} */}

            <Form.Item
              name="visitPrice"
              label={
                <span className={isMobile ? "text-sm" : ""}>Visit Price</span>
              }
              rules={[
                { required: true, message: "Please enter visit price" },
                { type: "number", min: 0, message: "Price must be positive" },
              ]}
            >
              <InputNumber
                placeholder="Enter visit price"
                className="w-full rounded-lg"
                size={isMobile ? "middle" : "large"}
                prefix="EGP"
                min={0}
                step={0.01}
                precision={2}
              />
            </Form.Item>

            {/* ── CREATE MODE: services panel ─────────────────────────── */}
            {!isEditMode && (
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <div
                    className={`font-medium text-gray-700 ${isMobile ? "text-sm" : ""}`}
                  >
                    Additional Services (Optional)
                  </div>
                  {!isAddingService && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => setIsAddingService(true)}
                      className="bg-primary text-white border-primary"
                    >
                      Add Service
                    </Button>
                  )}
                </div>

                {isAddingService && (
                  <AddServicePanel
                    newServiceData={newServiceData}
                    setNewServiceData={setNewServiceData}
                    services={services}
                    handleServiceTypeChange={handleServiceTypeChange}
                    handleServiceSelect={handleServiceSelect}
                    handleCancelAddService={handleCancelAddService}
                    handleAddServiceToInvoice={handleAddServiceToInvoice}
                    isAddServiceSubmitting={isAddServiceSubmitting}
                    getEffectivePrice={getEffectivePrice}
                  />
                )}
                <Spin spinning={servicesLoading}>
                  <div
                    className={`${isMobile ? "max-h-60" : "max-h-80"} overflow-y-auto border rounded-xl p-3 bg-gray-50`}
                  >
                    {services.length > 0 ? (
                      <div className="space-y-3">
                        {services.map((service) => (
                          <div
                            key={service.id}
                            className={`bg-white rounded-lg ${isMobile ? "p-3" : "p-4"} shadow-sm hover:shadow-md transition-shadow`}
                          >
                            <div className="flex items-start gap-3">
                              <Checkbox
                                checked={!!selectedServices[service.id]}
                                onChange={(e) =>
                                  handleServiceChange(
                                    service.id,
                                    e.target.checked,
                                  )
                                }
                              />
                              {service.photoID ? (
                                <img
                                  src={getImg(service.photoID)}
                                  className={`${isMobile ? "w-10 h-10" : "w-14 h-14"} rounded-lg object-cover shadow-sm`}
                                  alt={service.title}
                                />
                              ) : (
                                <MdOutlineMedicalServices
                                  className={`${isMobile ? "w-8 h-8" : "w-12 h-12"} text-primary rounded-lg object-cover shadow-sm`}
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <div
                                  className={`font-medium text-gray-800 ${isMobile ? "text-sm" : ""}`}
                                >
                                  {service.title}
                                </div>

                                {/* ✅ Discounted price display */}
                                <ServicePriceDisplay
                                  service={service}
                                  isMobile={isMobile}
                                />

                                {selectedServices[service.id] && (
                                  <div className="mt-2 space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <span
                                          className={`text-gray-600 block mb-1 ${isMobile ? "text-xs" : "text-sm"}`}
                                        >
                                          Unit Price:
                                        </span>
                                        <InputNumber
                                          min={0}
                                          value={
                                            selectedServices[service.id]
                                              .unitPrice
                                          }
                                          onChange={(val) =>
                                            handleUnitPriceChange(
                                              service.id,
                                              val,
                                            )
                                          }
                                          className="w-full"
                                          size="small"
                                          prefix="EGP"
                                          step={0.01}
                                          precision={2}
                                        />
                                      </div>
                                      <div>
                                        <span
                                          className={`text-gray-600 block mb-1 ${isMobile ? "text-xs" : "text-sm"}`}
                                        >
                                          Quantity:
                                        </span>
                                        <InputNumber
                                          min={1}
                                          max={100}
                                          value={
                                            selectedServices[service.id]
                                              .quantity
                                          }
                                          onChange={(val) =>
                                            handleQuantityChange(
                                              service.id,
                                              val,
                                            )
                                          }
                                          className="w-full"
                                          size="small"
                                        />
                                      </div>
                                    </div>
                                    <div
                                      className={`text-right text-blue-600 font-semibold ${isMobile ? "text-sm" : ""}`}
                                    >
                                      Subtotal: EGP{" "}
                                      {(
                                        selectedServices[service.id].quantity *
                                        selectedServices[service.id].unitPrice
                                      ).toFixed(2)}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}

                        {externalServices.map((ext) => (
                          <div
                            key={ext.tempId}
                            className={`bg-yellow-50 border border-yellow-300 rounded-lg ${isMobile ? "p-3" : "p-4"} shadow-sm`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div
                                  className={`font-medium text-gray-800 ${isMobile ? "text-sm" : ""}`}
                                >
                                  {ext.externalServiceTitle}
                                  <span className="ml-2 text-xs text-yellow-600 font-normal">
                                    (External)
                                  </span>
                                </div>
                                <div
                                  className={`text-gray-500 ${isMobile ? "text-xs" : "text-sm"}`}
                                >
                                  EGP {(ext.unitPrice ?? 0).toFixed(2)} × {ext.quantity} ={" "}
                                  {ext.quantity} = EGP{" "}
                                  {(ext.unitPrice * ext.quantity).toFixed(2)}
                                </div>
                              </div>
                              <Button
                                type="text"
                                size="small"
                                icon={<DeleteOutlined />}
                                danger
                                onClick={() =>
                                  removeExternalService(ext.tempId)
                                }
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 py-8">
                        <div
                          className={`${isMobile ? "text-2xl" : "text-4xl"} mb-2`}
                        >
                          🔍
                        </div>
                        <div className={isMobile ? "text-sm" : ""}>
                          No services available
                        </div>
                      </div>
                    )}
                  </div>
                </Spin>
              </div>
            )}

            {/* ── EDIT MODE: services panel ───────────────────────────── */}
            {isEditMode && invoiceData?.services && (
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <div
                    className={`font-medium text-gray-700 ${isMobile ? "text-sm" : ""}`}
                  >
                    Invoice Services
                  </div>
                  {!isAddingService && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => setIsAddingService(true)}
                      className="bg-primary text-white border-primary"
                    >
                      Add Service
                    </Button>
                  )}
                </div>

                {isAddingService && (
                  <AddServicePanel
                    newServiceData={newServiceData}
                    setNewServiceData={setNewServiceData}
                    services={services}
                    handleServiceTypeChange={handleServiceTypeChange}
                    handleServiceSelect={handleServiceSelect}
                    handleCancelAddService={handleCancelAddService}
                    handleAddServiceToInvoice={handleAddServiceToInvoice}
                    isAddServiceSubmitting={isAddServiceSubmitting}
                    getEffectivePrice={getEffectivePrice}
                  />
                )}

                <div className="border rounded-xl p-3 bg-gray-50 max-h-96 overflow-y-auto">
                  {invoiceData.services.length > 0 ? (
                    <div className="space-y-2">
                      {invoiceData.services.map((service) => {
                        const isEditing =
                          editingServiceId === service.invoiceServiceId;
                        const editData =
                          editServiceForm[service.invoiceServiceId];
                        return (
                          <div
                            key={service.invoiceServiceId}
                            className="p-3 bg-white rounded-lg border border-gray-200"
                          >
                            <div className="flex items-start gap-3">
                              {service.photoID && (
                                <img
                                  src={getImg(service.photoID)}
                                  className={`${isMobile ? "w-12 h-12" : "w-14 h-14"} rounded-lg object-cover`}
                                  alt={service.title}
                                />
                              )}
                              <div className="flex-1">
                                <div className="font-medium text-gray-800">
                                  {service.title}
                                </div>
                                {isEditing ? (
                                  <div className="mt-2 space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <span className="text-xs text-gray-600 block mb-1">
                                          Unit Price:
                                        </span>
                                        <InputNumber
                                          min={0}
                                          value={editData?.unitPrice}
                                          onChange={(val) =>
                                            updateEditServiceForm(
                                              service.invoiceServiceId,
                                              "unitPrice",
                                              val,
                                            )
                                          }
                                          className="w-full"
                                          size="small"
                                          prefix="EGP"
                                          step={0.01}
                                          precision={2}
                                        />
                                      </div>
                                      <div>
                                        <span className="text-xs text-gray-600 block mb-1">
                                          Quantity:
                                        </span>
                                        <InputNumber
                                          min={1}
                                          value={editData?.quantity}
                                          onChange={(val) =>
                                            updateEditServiceForm(
                                              service.invoiceServiceId,
                                              "quantity",
                                              val,
                                            )
                                          }
                                          className="w-full"
                                          size="small"
                                        />
                                      </div>
                                    </div>
                                    <div className="text-right text-blue-600 font-semibold text-sm">
                                      New Total: EGP{" "}
                                      {(
                                        (editData?.unitPrice || 0) *
                                        (editData?.quantity || 1)
                                      ).toFixed(2)}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-600 mt-1">
                                    EGP {(service.unitPrice ?? 0).toFixed(2)} ×{" "}
                                    {service.quantity} = EGP{" "}
                                    {(service.chargedAmount ?? 0).toFixed(2)}
                                  </div>
                                )}
                              </div>
                              <Space
                                direction="vertical"
                                className="flex"
                                size="small"
                              >
                                {isEditing ? (
                                  <Tooltip title="Save">
                                    <Button
                                      type="primary"
                                      size="small"
                                      icon={<SaveOutlined />}
                                      onClick={() =>
                                        handleSaveEditService(service)
                                      }
                                      loading={editInvoiceService.isPending}
                                      className="bg-green-500 border-green-500"
                                    >
                                      Save
                                    </Button>
                                  </Tooltip>
                                ) : (
                                  <>
                                    <Tooltip title="Edit Service">
                                      <Button
                                        type="text"
                                        size="small"
                                        icon={<EditOutlined />}
                                        onClick={() =>
                                          handleStartEditService(service)
                                        }
                                        className="text-blue-500"
                                      />
                                    </Tooltip>
                                    <Popconfirm
                                      title="Delete Service"
                                      description="Are you sure?"
                                      onConfirm={() =>
                                        handleDeleteService(
                                          service.invoiceServiceId,
                                        )
                                      }
                                      okText="Yes"
                                      cancelText="No"
                                      okButtonProps={{ danger: true }}
                                    >
                                      <Tooltip title="Delete Service">
                                        <Button
                                          type="text"
                                          size="small"
                                          icon={<DeleteOutlined />}
                                          danger
                                          loading={
                                            removeInvoiceService.isPending
                                          }
                                        />
                                      </Tooltip>
                                    </Popconfirm>
                                  </>
                                )}
                              </Space>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-3">
                      <div className={isMobile ? "text-sm" : ""}>
                        No services added yet
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Summary */}
            <div className={`mb-4 p-4 bg-gradient-to-r ${totals.remaining === 0 && totals.total > 0 ? "from-green-50 to-green-50 rounded-lg border border-green-200" : "from-blue-50 to-indigo-50 rounded-lg border border-blue-200"} from-blue-50 to-indigo-50 rounded-lg border border-blue-200`}>
              <div
                className={`font-semibold text-gray-700 mb-3 ${isMobile ? "text-sm" : ""}`}
              >
                Invoice Summary
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span
                    className={`text-gray-600 ${isMobile ? "text-xs" : "text-sm"}`}
                  >
                    Visit Price:
                  </span>
                  <span
                    className={`font-semibold text-blue-600 ${isMobile ? "text-sm" : ""}`}
                  >
                    EGP {totals.visitPrice.toFixed(2)}
                  </span>
                </div>
                {totals.servicesTotal > 0 && (
                  <div className="flex justify-between items-center">
                    <span
                      className={`text-gray-600 ${isMobile ? "text-xs" : "text-sm"}`}
                    >
                      Services Total:
                    </span>
                    <span
                      className={`font-semibold text-blue-600 ${isMobile ? "text-sm" : ""}`}
                    >
                      EGP {totals.servicesTotal.toFixed(2)}
                    </span>
                  </div>
                )}
                <Divider className="my-2" />
                <div className="space-y-2">
                  {totals.totalDiscount > 0 && (
                    <div className="flex justify-between items-center">
                      <span
                        className={`text-gray-600 ${isMobile ? "text-xs" : "text-sm"}`}
                      >
                        Total Discount:
                      </span>
                      <span
                        className={`font-semibold text-red-600 ${isMobile ? "text-sm" : ""}`}
                      >
                        -EGP {totals.totalDiscount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span
                      className={`text-gray-800 font-bold ${isMobile ? "text-base" : "text-lg"}`}
                    >
                      Final Total:
                    </span>
                    <span
                      className={`font-bold text-green-600 ${isMobile ? "text-lg" : "text-xl"}`}
                    >
                      EGP {totals.total.toFixed(2)}
                    </span>
                  </div>
                  {totals.total > 0 && (
                    <div className={`}`}>
                      <div className="flex justify-between items-center">
                        <span
                          className={`font-bold ${isMobile ? "text-base" : "text-lg"} ${totals.remaining > 0 ? "text-orange-800" : "text-emerald-800"}`}
                        >
                          Remaining Balance:
                        </span>
                        <span
                          className={`font-bold ${isMobile ? "text-lg" : "text-xl"} ${totals.remaining > 0 ? "text-orange-600" : "text-emerald-600"}`}
                        >
                          EGP {totals.remaining.toFixed(2)}
                        </span>
                      </div>
                      {totals.remaining === 0 && totals.total > 0 && (
                        <div className="text-center text-emerald-600 text-sm mt-2">
                          ✓ Fully Paid
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>


            {isEditMode && previouslyPaidAmount > 0 && (
              <div className="mb-4 p-3 bg-primary/5 rounded-lg border border-primary/80">
                <div className="flex justify-between items-center">
                  <span
                    className={`text-primary font-medium ${isMobile ? "text-sm" : ""}`}
                  >
                    Previously Paid:
                  </span>
                  <span
                    className={`font-bold text-primary_hover ${isMobile ? "text-base" : "text-lg"}`}
                  >
                    EGP {previouslyPaidAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* <div
              className={`grid ${isMobile ? "grid-cols-1 gap-3" : "grid-cols-2 gap-4"}`}
            >
              <Form.Item
                name="discountAmount"
                label={
                  <span className={isMobile ? "text-sm" : ""}>
                    Discount Amount (EGP)
                  </span>
                }
                rules={[
                  {
                    type: "number",
                    min: 0,
                    message: "Discount must be positive",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Flat discount"
                  className="w-full rounded-lg"
                  size={isMobile ? "middle" : "large"}
                  prefix="EGP"
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>
              <Form.Item
                name="discountPer"
                label={
                  <span className={isMobile ? "text-sm" : ""}>
                    Discount Percentage (%)
                  </span>
                }
                rules={[
                  {
                    type: "number",
                    min: 0,
                    max: 100,
                    message: "Must be between 0-100",
                  },
                ]}
              >
                <InputNumber
                  placeholder="% discount"
                  className="w-full rounded-lg"
                  size={isMobile ? "middle" : "large"}
                  prefix="%"
                  min={0}
                  max={100}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>
            </div> */}

            {/* <Form.Item
              name="paidAmount"
              label={
                <span className={isMobile ? "text-sm" : ""}>
                  {isEditMode ? "Additional Payment" : "Paid Amount"}
                </span>
              }
              rules={[
                {
                  required: true,
                  message: `Please enter ${isEditMode ? "additional payment" : "paid amount"}`,
                },
                { type: "number", min: 0, message: "Amount must be positive" },
              ]}
            >
              <InputNumber
                placeholder={
                  isEditMode ? "Enter additional payment" : "Enter paid amount"
                }
                className="w-full rounded-lg"
                size={isMobile ? "middle" : "large"}
                prefix="EGP"
                min={0}
                step={0.01}
                precision={2}
              />
            </Form.Item> */}

            {/* {totals.total > 0 && (
              <div
                className={`p-4 rounded-lg border-2 ${totals.remaining > 0 ? "bg-orange-50 border-orange-300" : "bg-emerald-50 border-emerald-300"}`}
              >
                <div className="flex justify-between items-center">
                  <span
                    className={`font-bold ${isMobile ? "text-base" : "text-lg"} ${totals.remaining > 0 ? "text-orange-800" : "text-emerald-800"}`}
                  >
                    Remaining Balance:
                  </span>
                  <span
                    className={`font-bold ${isMobile ? "text-lg" : "text-xl"} ${totals.remaining > 0 ? "text-orange-600" : "text-emerald-600"}`}
                  >
                    EGP {totals.remaining.toFixed(2)}
                  </span>
                </div>
                {totals.remaining === 0 && totals.total > 0 && (
                  <div className="text-center text-emerald-600 text-sm mt-2">
                    ✓ Fully Paid
                  </div>
                )}
              </div>
            )} */}
          </Form>
        </Spin>
      </Modal>

      <PrintConfirmModal
        visible={printConfirmVisible}
        onPrint={handlePrintConfirm}
        onSkip={handlePrintSkip}
        isMobile={isMobile}
      />
      <CouponApplyModal
        visible={couponModalVisible}
        invoiceId={pendingInvoiceId}
        applliedCoupoun={invoiceData?.applliedCoupoun}
        invoiceTotal={pendingInvoiceTotal}
        isMobile={isMobile}
        onDone={(paymentResult) => {
          if (paymentResult && lastCreatedInvoiceData) {
            const { paidAmount: paid, couponDiscount } = paymentResult;
            const prev = lastCreatedInvoiceData.invoice;

            setLastCreatedInvoiceData({
              ...lastCreatedInvoiceData,
              invoice: {
                ...prev,
                paidAmount: paid,
                discountAmount: (prev.discountAmount || 0) + (couponDiscount || 0),
              },
            });
          }

          setCouponModalVisible(false);
          setPendingInvoiceId(null);
          setPendingInvoiceTotal(0);
          setPrintConfirmVisible(true);
        }}
        invoiceData={invoiceData}
      />
    </>
  );
};

export default InvoiceModal;
